import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import HotelIcon from "@mui/icons-material/Hotel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TextField, Box } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  HOSTEL_ADMISSION_RULES,
  HOSTEL_CONSENT_VERSION,
} from "../constants/admissionConsent";

const API = process.env.REACT_APP_API_URL;

const HostelBookingForm = ({ bedId, open, onClose, onSuccess }) => {
  const [bed, setBed] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [purposeOfJoining, setPurposeOfJoining] = useState("");
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [errors, setErrors] = useState({});
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!bedId || !open) return;

    // Clear form data when opening
    setStartDate("");
    setPurposeOfJoining("");
    setAadhaarFront(null);
    setAadhaarBack(null);
    setStudentPhoto(null);
    setConfirmOpen(false);
    setConsentAccepted(false);

    const fetchData = async () => {
      const loadingToast = toast.loading("Loading...");
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const [bedRes, profileRes] = await Promise.all([
          axios.get(`${API}hostel/beds/${bedId}/`, config),
          axios.get(`${API}users/me/`, config),
        ]);
        setBed(bedRes.data);
        setStudent(profileRes.data);
        setBillingSummary(null);
        toast.dismiss(loadingToast);
        setLoading(false);
      } catch (err) {
        toast.dismiss(loadingToast);
        toast.error("❌ Failed to load booking form data.");
        onClose();
      }
    };

    fetchData();
  }, [bedId, open, onClose]);

  useEffect(() => {
    if (!open || !bedId || !startDate) {
      setBillingSummary(null);
      return;
    }

    const fetchBillingSummary = async () => {
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(
          `${API}hostel/billing-summary/?bed_id=${bedId}&start_date=${startDate}`,
          config
        );
        setBillingSummary(response.data);
      } catch (err) {
        setBillingSummary(null);
        toast.error("Failed to calculate billing for the selected date.");
      }
    };

    fetchBillingSummary();
  }, [bedId, open, startDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    // Validate required fields
    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!purposeOfJoining.trim()) {
      newErrors.purposeOfJoining = "Purpose of joining is required";
    }

    // Only require Aadhaar photos if student doesn't have them
    if (!student?.aadhaar_front_photo && !aadhaarFront) {
      newErrors.aadhaarFront = "Aadhaar front photo is required";
    }

    if (!student?.aadhaar_back_photo && !aadhaarBack) {
      newErrors.aadhaarBack = "Aadhaar back photo is required";
    }

    if (!student?.profile_photo && !studentPhoto) {
      newErrors.studentPhoto = "Profile photo is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setConfirmOpen(true);
  };

  const submitFinalBooking = async () => {
    if (!consentAccepted || isSubmitting) return;

    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const loadingToast = toast.loading("Loading...");
    setIsSubmitting(true);
    try {
      if (!student?.profile_photo && studentPhoto) {
        const photoForm = new FormData();
        photoForm.append("profile_photo", studentPhoto);
        await axios.patch(`${API}users/update-profile-photo/`, photoForm, {
          ...config,
          headers: {
            ...config.headers,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      const formData = new FormData();
      formData.append("bed_id", bedId);
      formData.append("start_date", startDate);
      formData.append("purpose_of_joining", purposeOfJoining);
      formData.append("consent_accepted", "true");

      // Only append Aadhaar photos if they are provided
      if (aadhaarFront) {
        formData.append("aadhaar_front_photo", aadhaarFront);
      }
      if (aadhaarBack) {
        formData.append("aadhaar_back_photo", aadhaarBack);
      }

      await axios.post(`${API}hostel/bookings/`, formData, {
        ...config,
        headers: { ...config.headers, "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(loadingToast);
      toast.success("✅ Booking request submitted!");
      setConfirmOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      const responseData = err.response?.data;
      const firstError =
        responseData && typeof responseData === "object"
          ? Object.values(responseData).flat().find(Boolean)
          : null;
      const msg =
        responseData?.error ||
        responseData?.detail ||
        firstError ||
        "❌ Booking failed. Check your input.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const bookingDetails = [
    // { label: "Room", value: bed?.room?.room_number },
    // { label: "Bed", value: bed?.bed_number },
    { label: "Monthly Fee", value: `₹${billingSummary?.monthly_fee || 0}` },
    {
      label: "Deposit",
      value: `₹${billingSummary?.deposit_amount || 0}`,
    },
    {
      label: "Total",
      value: `₹${billingSummary?.total || 0}`,
    },
  ];

  const FileUploadButton = ({
    label,
    onChange,
    required = false,
    accept = "image/png,image/jpg,image/jpeg",
    value = null,
    error = null,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={onChange}
          className="hidden"
          id={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          name={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
        />
        <label
          htmlFor={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          className={`flex flex-col items-center justify-center gap-1 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
            error
              ? "border-red-500 bg-red-50 text-red-700"
              : value
              ? "border-green-500 bg-green-50 text-green-700"
              : "border-gray-300 text-gray-600 hover:border-blue-400"
          }`}
        >
          {value ? (
            <>
              <CheckCircleIcon className="text-green-600" />
              <span className="font-medium">{value.name}</span>
            </>
          ) : (
            <>
              <CloudUploadIcon />
              <span className="font-medium">Click to upload</span>
              <span className="text-xs text-gray-500">
                Max file size: 2MB | Accepted: PNG, JPG, JPEG
              </span>
            </>
          )}
        </label>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6">
          <DialogTitle className="flex items-center gap-3 text-white font-bold text-xl">
            <HotelIcon className="text-2xl" />
            Book Hostel Bed
          </DialogTitle>
        </div>

        <DialogContent className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Loading booking details...
              </Typography>
            </Box>
          ) : (
            <form
              id="hostel-booking-form"
              onSubmit={handleSubmit}
              className="space-y-6"
              noValidate
            >
              {/* Bed Information Card */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                <Typography className="text-lg font-semibold text-gray-800 mb-2">
                  Bed Information
                </Typography>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Room:</span>
                    <span className="font-semibold text-blue-600 ml-2">
                      {bed?.room?.room_number}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Bed:</span>
                    <span className="font-semibold text-blue-600 ml-2">
                      {bed?.bed_number}
                    </span>
                  </div>
                </div>
              </div>

              {/* Booking Details Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <Typography className="text-lg font-semibold text-gray-800 mb-4">
                  Booking Details
                </Typography>

                <div className="space-y-4 mt-4">
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (errors.startDate) {
                        setErrors((prev) => ({ ...prev, startDate: null }));
                      }
                    }}
                    inputProps={{
                      min: new Date().toISOString().split("T")[0],
                    }}
                    className="mb-4"
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                  />

                  <TextField
                    fullWidth
                    type="text"
                    label="Purpose of joining"
                    value={purposeOfJoining}
                    onChange={(e) => {
                      setPurposeOfJoining(e.target.value);
                      if (errors.purposeOfJoining) {
                        setErrors((prev) => ({
                          ...prev,
                          purposeOfJoining: null,
                        }));
                      }
                    }}
                    required
                    multiline
                    rows={3}
                    className="mb-4"
                    error={!!errors.purposeOfJoining}
                    helperText={errors.purposeOfJoining}
                  />
                </div>
              </div>

              {/* Document Upload Card */}
              {(!student?.aadhaar_front_photo ||
                !student?.aadhaar_back_photo ||
                !student?.profile_photo) && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <Typography className="text-lg font-semibold text-gray-800 mb-4">
                    Required Documents
                  </Typography>

                  <div className="space-y-4 mt-4">
                    {!student?.aadhaar_front_photo && (
                      <FileUploadButton
                        label="Aadhaar Front Photo"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const maxSize = 2 * 1024 * 1024;
                          if (file && file.size > maxSize) {
                            toast.error("File must be less than 2MB");
                            e.target.value = null;
                          } else {
                            setAadhaarFront(file);
                            if (errors.aadhaarFront) {
                              setErrors((prev) => ({
                                ...prev,
                                aadhaarFront: null,
                              }));
                            }
                          }
                        }}
                        required
                        value={aadhaarFront}
                        error={errors.aadhaarFront}
                      />
                    )}

                    {!student?.aadhaar_back_photo && (
                      <FileUploadButton
                        label="Aadhaar Back Photo"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const maxSize = 2 * 1024 * 1024;
                          if (file && file.size > maxSize) {
                            toast.error("File must be less than 2MB");
                            e.target.value = null;
                          } else {
                            setAadhaarBack(file);
                            if (errors.aadhaarBack) {
                              setErrors((prev) => ({
                                ...prev,
                                aadhaarBack: null,
                              }));
                            }
                          }
                        }}
                        required
                        value={aadhaarBack}
                        error={errors.aadhaarBack}
                      />
                    )}

                    {!student?.profile_photo && (
                      <FileUploadButton
                        label="Profile Photo"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const maxSize = 2 * 1024 * 1024;
                          if (file && file.size > maxSize) {
                            toast.error("Profile photo must be less than 2MB");
                            e.target.value = null;
                          } else {
                            setStudentPhoto(file);
                            if (errors.studentPhoto) {
                              setErrors((prev) => ({
                                ...prev,
                                studentPhoto: null,
                              }));
                            }
                          }
                        }}
                        required
                        value={studentPhoto}
                        error={errors.studentPhoto}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Billing Summary Card */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                <Typography className="text-lg font-semibold text-gray-800 mb-4">
                  Billing Summary
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  {bookingDetails.map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        {item.label}
                      </div>
                      <div className="text-xl font-bold text-gray-800">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          )}
        </DialogContent>

        <DialogActions className="justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button onClick={onClose} variant="outlined" className="px-6 py-2">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="hostel-booking-form"
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            Submit Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6">
          <DialogTitle className="text-white font-bold text-xl">
            Confirm Booking
          </DialogTitle>
        </div>

        <DialogContent className="p-6 space-y-3">
          <div className="bg-gray-50 p-4 rounded-lg">
            <Typography className="font-semibold text-gray-800 mb-3">
              Bussiness Track Hostel & Library
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">
                  {student?.first_name} {student?.last_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{student?.phone_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{student?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Address:</span>
                <span className="font-medium">{student?.address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Room:</span>
                <span className="font-medium">{bed?.room?.room_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bed:</span>
                <span className="font-medium">{bed?.bed_number}</span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <Typography className="font-semibold text-gray-800 mb-2">
              Payment Summary
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Fee:</span>
                <span className="font-medium">
                  ₹{billingSummary?.monthly_fee || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit:</span>
                <span className="font-medium">
                  ₹{billingSummary?.deposit_amount || 0}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-800 font-semibold">Total:</span>
                <span className="text-lg font-bold text-green-600">
                  ₹{billingSummary?.total || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <Typography className="font-semibold text-gray-800 mb-2">
              Hostel Admission Rules &amp; Consent
            </Typography>
            <Typography variant="body2" className="text-gray-600 mb-3">
              Please read every rule before accepting. Consent is compulsory for every booking.
            </Typography>
            <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white p-3">
              <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
                {HOSTEL_ADMISSION_RULES.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ol>
            </div>
            <FormControlLabel
              className="mt-3 items-start"
              control={
                <Checkbox
                  checked={consentAccepted}
                  onChange={(event) => setConsentAccepted(event.target.checked)}
                  color="primary"
                />
              }
              label={`I have read, understood, and agree to all Hostel admission rules (Version ${HOSTEL_CONSENT_VERSION}).`}
            />
          </div>
        </DialogContent>

        <DialogActions className="p-6">
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            className="px-6 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={submitFinalBooking}
            disabled={!consentAccepted || isSubmitting}
            variant="contained"
            color="primary"
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
          >
            {isSubmitting ? "Booking..." : "Confirm & Book"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HostelBookingForm;
